/**
 * Clipboard Utilities
 * ---
 * Handles clipboard operations across web and native platforms.
 *
 * IMPORTANT: On web (especially Safari/WebKit), clipboard operations must be
 * called synchronously within the user gesture context. Do NOT await any
 * operations before calling these functions from click handlers.
 *
 * @see https://bugs.webkit.org/show_bug.cgi?id=222262
 */
import { Platform } from 'react-native';
import * as Clipboard from 'expo-clipboard';

/**
 * Legacy clipboard copy using execCommand.
 * This must run synchronously in the user gesture context.
 */
function copyWithExecCommand(text: string): boolean {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  // Prevent scrolling to bottom
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '0';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

/**
 * Web-specific clipboard implementation.
 * Tries navigator.clipboard first, falls back to execCommand.
 */
async function copyToClipboardWeb(text: string): Promise<boolean> {
  // Try modern Clipboard API
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy method
    }
  }

  // Fallback: Legacy execCommand method
  return copyWithExecCommand(text);
}

/**
 * Copies text to clipboard with cross-platform support.
 *
 * On web, uses navigator.clipboard.writeText with fallback to execCommand.
 * On native, uses expo-clipboard.
 *
 * @param text - The text to copy
 * @returns Promise resolving to true on success, false on failure
 *
 * @example
 * // In a click handler - call directly, don't await before calling
 * const handleCopy = () => {
 *   copyToClipboard(text).then(success => {
 *     if (success) showToast('success', 'Copied!');
 *     else showToast('error', 'Failed to copy');
 *   });
 * };
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  try {
    if (Platform.OS === 'web') {
      // Web: Try modern API first, fall back to legacy
      return await copyToClipboardWeb(text);
    }
    // Native: Use expo-clipboard
    await Clipboard.setStringAsync(text);
    return true;
  } catch {
    return false;
  }
}
